import React, { createContext, useContext, useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Feature, UsageLimits, FEATURE_NAMES } from "@/lib/featureConfig";
import { SubscriptionTier } from "@/lib/subscriptionTiers";

// Default configurations (fallback)
const DEFAULT_TIER_FEATURES: Record<SubscriptionTier, Feature[]> = {
  starter: ["ai_roleplay", "basic_scenarios"],
  pro: [
    "ai_roleplay", "voice_mode", "video_mode", "detailed_feedback",
    "basic_scenarios", "all_scenarios", "progress_analytics", "buyer_practice",
  ],
  topCloser: [
    "ai_roleplay", "voice_mode", "video_mode", "detailed_feedback",
    "basic_scenarios", "all_scenarios", "progress_analytics", "buyer_practice",
    "advanced_coaching", "personalized_plans", "competition_scenarios",
    "priority_support", "call_grading",
  ],
  enterprise: [
    "ai_roleplay", "voice_mode", "video_mode", "detailed_feedback",
    "basic_scenarios", "all_scenarios", "progress_analytics", "buyer_practice",
    "advanced_coaching", "personalized_plans", "competition_scenarios",
    "priority_support", "call_grading", "custom_scenarios", "api_access",
    "unlimited_seats", "custom_integrations", "ai_calling", "team_management",
  ],
};

const DEFAULT_TIER_LIMITS: Record<SubscriptionTier, UsageLimits> = {
  starter: { roleplaysPerMonth: 15, customScenariosLimit: 0, teamMembersLimit: 0 },
  pro: { roleplaysPerMonth: 50, customScenariosLimit: 5, teamMembersLimit: 0 },
  topCloser: { roleplaysPerMonth: 150, customScenariosLimit: 20, teamMembersLimit: 5 },
  enterprise: { roleplaysPerMonth: -1, customScenariosLimit: -1, teamMembersLimit: -1 },
};

interface FeatureConfigContextType {
  tierFeatures: Record<SubscriptionTier, Feature[]>;
  tierLimits: Record<SubscriptionTier, UsageLimits>;
  isLoading: boolean;
  // Helper functions
  tierHasFeature: (tier: SubscriptionTier | null, feature: Feature) => boolean;
  getTierFeatures: (tier: SubscriptionTier | null) => Feature[];
  getTierLimits: (tier: SubscriptionTier | null) => UsageLimits;
  getMinimumTierForFeature: (feature: Feature) => SubscriptionTier | null;
}

const FeatureConfigContext = createContext<FeatureConfigContextType | null>(null);

export function FeatureConfigProvider({ children }: { children: React.ReactNode }) {
  const [tierFeatures, setTierFeatures] = useState<Record<SubscriptionTier, Feature[]>>(DEFAULT_TIER_FEATURES);
  const [tierLimits, setTierLimits] = useState<Record<SubscriptionTier, UsageLimits>>(DEFAULT_TIER_LIMITS);
  const [isLoading, setIsLoading] = useState(false);

  // Load config from database
  useEffect(() => {
    const loadConfig = async () => {
      try {
        const { data, error } = await supabase
          .from("app_settings")
          .select("key, value")
          .in("key", ["tier_features", "tier_limits"]);

        if (error) {
          console.error("Error loading feature config:", error);
          return;
        }

        if (data) {
          for (const setting of data) {
            if (setting.key === "tier_features" && setting.value) {
              setTierFeatures(setting.value as unknown as Record<SubscriptionTier, Feature[]>);
            } else if (setting.key === "tier_limits" && setting.value) {
              setTierLimits(setting.value as unknown as Record<SubscriptionTier, UsageLimits>);
            }
          }
        }
      } catch (err) {
        console.error("Error loading feature config:", err);
      } finally {
        setIsLoading(false);
      }
    };

    loadConfig();

    // Subscribe to real-time updates
    const channel = supabase
      .channel("feature-config-changes")
      .on(
        "postgres_changes",
        {
          event: "*",
          schema: "public",
          table: "app_settings",
          filter: "key=in.(tier_features,tier_limits)",
        },
        (payload) => {
          const newRecord = payload.new as { key?: string; value?: unknown } | null;
          if (newRecord?.key === "tier_features" && newRecord.value) {
            setTierFeatures(newRecord.value as unknown as Record<SubscriptionTier, Feature[]>);
          } else if (newRecord?.key === "tier_limits" && newRecord.value) {
            setTierLimits(newRecord.value as unknown as Record<SubscriptionTier, UsageLimits>);
          }
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, []);

  // Helper: Check if a tier has access to a feature
  const tierHasFeature = (tier: SubscriptionTier | null, feature: Feature): boolean => {
    if (!tier) return false;
    return tierFeatures[tier]?.includes(feature) ?? false;
  };

  // Helper: Get all features for a tier
  const getTierFeatures = (tier: SubscriptionTier | null): Feature[] => {
    if (!tier) return [];
    return tierFeatures[tier] ?? [];
  };

  // Helper: Get usage limits for a tier
  const getTierLimits = (tier: SubscriptionTier | null): UsageLimits => {
    if (!tier) {
      return { roleplaysPerMonth: 3, customScenariosLimit: 0, teamMembersLimit: 0 };
    }
    return tierLimits[tier] ?? DEFAULT_TIER_LIMITS[tier];
  };

  // Helper: Get minimum tier for a feature
  const getMinimumTierForFeature = (feature: Feature): SubscriptionTier | null => {
    const tierOrder: SubscriptionTier[] = ["starter", "pro", "topCloser", "enterprise"];
    for (const tier of tierOrder) {
      if (tierFeatures[tier]?.includes(feature)) {
        return tier;
      }
    }
    return null;
  };

  return (
    <FeatureConfigContext.Provider
      value={{
        tierFeatures,
        tierLimits,
        isLoading,
        tierHasFeature,
        getTierFeatures,
        getTierLimits,
        getMinimumTierForFeature,
      }}
    >
      {children}
    </FeatureConfigContext.Provider>
  );
}

export function useFeatureConfig() {
  const context = useContext(FeatureConfigContext);
  if (!context) {
    throw new Error("useFeatureConfig must be used within a FeatureConfigProvider");
  }
  return context;
}

// Re-export for convenience
export { FEATURE_NAMES };
export type { Feature, UsageLimits };
